import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  Star,
  Crown,
  Zap,
  Shield,
  Clock,
  Award,
  ChevronRight,
} from 'lucide-react';
import GlassCard from '../components/GlassCard';
import { packages } from '../data/packages';

const PackagesPage = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedCarType, setSelectedCarType] = useState('sedan');

  const content = {
    title: isAr ? 'باقاتنا المتميزة' : 'Premium Packages',
    subtitle: isAr
      ? 'اختر من بين باقاتنا المصممة باحتراف لتناسب كل احتياج وميزانية'
      : 'Choose from our expertly crafted packages for every need and budget',
    carTypes: {
      sedan: isAr ? 'سيدان' : 'Sedan',
      suv5: isAr ? 'جيب / SUV' : 'SUV',
      '7-seater': isAr ? '٧ ركاب' : '7-Seater',
    },
    popular: isAr ? 'الأكثر طلباً' : 'Popular',
    currency: isAr ? 'د.ك' : 'KD',
    startingPrice: isAr
      ? 'سعر البداية لخدمة المرة الواحدة'
      : 'Starting price for one-time service',
    compareLink: isAr ? 'قارن المميزات' : 'Compare features',
    comparisonTitle: isAr ? 'مقارنة الباقات' : 'Package Comparison',
    featureCol: isAr ? 'المميزات' : 'Features',
    addOnsTitle: isAr ? 'خدمات إضافية' : 'Additional Services',
    addToPkg: isAr ? 'أضف للباقة' : 'Add to Package',
    benefits: [
      {
        icon: <Shield className='h-8 w-8 text-white' />,
        title: isAr ? 'ضمان الجودة' : 'Quality Guarantee',
        description: isAr
          ? 'منتجات بريميوم وفنيين معتمدين'
          : 'Premium products and certified technicians',
      },
      {
        icon: <Clock className='h-8 w-8 text-white' />,
        title: isAr ? 'توقيت مرن' : 'Flexible Timing',
        description: isAr
          ? '١٠ صباحاً - ١٠ مساءً، طوال الأسبوع'
          : '10 AM - 10 PM, 7 days a week',
      },
      {
        icon: <Award className='h-8 w-8 text-white' />,
        title: isAr ? 'خدمة حائزة على جوائز' : 'Award Winning',
        description: isAr ? 'جائزة أفضل خدمة ٢٠٢٣' : 'Best Service Award 2023',
      },
      {
        icon: (
          <ChevronRight
            className={`h-8 w-8 text-white ${isAr ? 'rotate-180' : ''}`}
          />
        ),
        title: isAr ? 'خدمة سريعة' : 'Quick Service',
        description: isAr
          ? '٤٥-٦٠ دقيقة متوسط وقت الخدمة'
          : '45-60 minutes average service time',
      },
    ],
  };

  const featuresComparison = [
    {
      feature: isAr ? 'غسيل فوم (رغوة الثلج)' : 'Snow Foam Contact Wash',
      normal: true,
      shine: true,
      vip: true,
      royal: true,
    },
    {
      feature: isAr ? 'شامبو فوم عازل للماء' : 'Hydrophobic Foam Shampoo',
      normal: true,
      shine: true,
      vip: true,
      royal: true,
    },
    {
      feature: isAr
        ? 'تلميع تواير وحماية الزوائد'
        : 'Trim Dressing & High Gloss Tire Shine',
      normal: false,
      shine: true,
      vip: true,
      royal: true,
    },
    {
      feature: isAr
        ? 'تلميع داخلي عميق (شامل)'
        : 'Deep Interior Detailing (Full)',
      normal: false,
      shine: false,
      vip: true,
      royal: true,
    },
    {
      feature: isAr
        ? 'تنظيف المحرك وإزالة البقع'
        : 'Engine Cleaning & Hard Spots Removal',
      normal: false,
      shine: false,
      vip: true,
      royal: true,
    },
    {
      feature: isAr
        ? 'بوليش مرحلة واحدة وحماية جرافين'
        : '1 Step Polish & Graphene Coating',
      normal: false,
      shine: false,
      vip: false,
      royal: true,
    },
  ];

  const addOns = [
    { name: isAr ? 'تلميع واكس' : 'Waxing', price: isAr ? '٤ د.ك' : 'KD 4' },
    {
      name: isAr ? 'بوليش احترافي' : 'Polishing',
      price: isAr ? '١٢ د.ك' : 'KD 12',
    },
    {
      name: isAr ? 'عناية الكراسي الجلد' : 'Leather Treatment',
      price: isAr ? '٣ د.ك' : 'KD 3',
    },
    {
      name: isAr ? 'بوليش زجاج' : 'Window Polishing',
      price: isAr ? '٦ د.ك' : 'KD 6',
    },
    {
      name: isAr ? 'تعقيم التكييف' : 'AC Sanitizing',
      price: isAr ? '٩ د.ك' : 'KD 9',
    },
  ];

  const openWhatsApp = (packageName) => {
    const carLabel = content.carTypes[selectedCarType];
    const message = encodeURIComponent(
      isAr
        ? `مرحباً سوبر كلاسيكي، أود حجز باقة ${packageName} لسيارة ${carLabel}.`
        : `Hi Super Classy, I want to book ${packageName} package for ${selectedCarType}.`,
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  return (
    <div className='pt-32' dir={isAr ? 'rtl' : 'ltr'}>
      <div className='section-padding py-20'>
        <div className='max-w-7xl mx-auto'>
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className='text-center mb-12'
          >
            <h1 className='text-5xl md:text-7xl font-black mb-6 text-gradient'>
              {content.title}
            </h1>
            <p className='text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed'>
              {content.subtitle}
            </p>
          </motion.div>

          {/* Car Type Selection */}
          <div className='flex justify-center mb-16'>
            <div className='inline-flex p-1.5 glass-card rounded-full shadow-lg border border-white/10'>
              {['sedan', 'suv5', '7-seater'].map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedCarType(type)}
                  className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
                    selectedCarType === type
                      ? 'premium-gradient text-white shadow-md scale-105'
                      : 'text-gray-500'
                  }`}
                >
                  {content.carTypes[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Package Cards */}
          <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20'>
            {packages.map((pkg, index) => {
              const price = pkg.prices[selectedCarType];
              // Using different icons for variety
              const Icon = index === 2 ? Crown : index === 3 ? Star : Zap;
              const pName = isAr ? pkg.nameAr || pkg.name : pkg.name;

              return (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <GlassCard
                    className={`h-full p-6 flex flex-col ${pkg.popular ? 'border-2 border-royal-purple-500 shadow-2xl' : ''}`}
                    lang={lang}
                  >
                    <div className='flex items-center justify-between mb-6'>
                      <div className='p-3 premium-gradient rounded-xl shadow-md'>
                        <Icon className='h-6 w-6 text-white' />
                      </div>
                      {pkg.popular && (
                        <span className='px-3 py-1 bg-royal-purple-500/20 text-royal-purple-600 rounded-full text-xs font-bold'>
                          {content.popular}
                        </span>
                      )}
                    </div>

                    <h3 className='text-xl font-black mb-2'>{pName}</h3>

                    <div className='mb-6'>
                      <div
                        className={`flex items-baseline gap-1 ${isAr ? 'flex-row-reverse' : ''}`}
                      >
                        <span className='text-3xl font-black text-gradient'>
                          {price}
                        </span>
                        <span className='text-lg font-bold'>
                          {content.currency}
                        </span>
                      </div>
                      <p className='text-[10px] font-bold text-gray-400 uppercase tracking-widest'>
                        {content.startingPrice}
                      </p>
                    </div>

                    <div className='space-y-3 mb-8 flex-grow'>
                      {(isAr ? pkg.featuresAr : pkg.features)
                        .slice(0, 6)
                        .map((f, i) => (
                          <div
                            key={i}
                            className='flex items-start gap-2 text-sm font-medium'
                          >
                            <Check className='h-4 w-4 text-green-500 flex-shrink-0 mt-0.5' />
                            <span className='text-gray-600 dark:text-gray-300'>
                              {f}
                            </span>
                          </div>
                        ))}
                    </div>

                    <button
                      onClick={() => openWhatsApp(pName)}
                      className={`w-full py-3 rounded-xl font-black transition-all ${pkg.popular ? 'premium-gradient text-white' : 'glass-button'}`}
                    >
                      {isAr ? 'حجز ' : 'Book '}
                      {pName}
                    </button>
                  </GlassCard>{' '}
                  {/* <--- THIS WAS MISSING */}
                </motion.div>
              );
            })}
          </div>

          {/* Comparison Table */}
          <GlassCard className='p-8 mb-20 overflow-hidden' lang={lang}>
            <h2 className='text-3xl font-black mb-8 text-center text-gradient'>
              {content.comparisonTitle}
            </h2>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm font-medium'>
                <thead>
                  <tr className='border-b border-gray-100 dark:border-gray-800'>
                    <th className={`py-4 ${isAr ? 'text-right' : 'text-left'}`}>
                      {content.featureCol}
                    </th>
                    <th className='text-center py-4'>Normal</th>
                    <th className='text-center py-4'>Shine</th>
                    <th className='text-center py-4'>VIP</th>
                    <th className='text-center py-4'>Royal</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-50 dark:divide-gray-900'>
                  {featuresComparison.map((row, i) => (
                    <tr
                      key={i}
                      className='hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors'
                    >
                      <td className='py-4'>{row.feature}</td>
                      <td className='text-center'>
                        <Check
                          className={`mx-auto h-5 w-5 ${row.normal ? 'text-green-500' : 'text-gray-200'}`}
                        />
                      </td>
                      <td className='text-center'>
                        <Check
                          className={`mx-auto h-5 w-5 ${row.shine ? 'text-green-500' : 'text-gray-200'}`}
                        />
                      </td>
                      <td className='text-center'>
                        <Check
                          className={`mx-auto h-5 w-5 ${row.vip ? 'text-green-500' : 'text-gray-200'}`}
                        />
                      </td>
                      <td className='text-center'>
                        <Check
                          className={`mx-auto h-5 w-5 ${row.royal ? 'text-green-500' : 'text-gray-200'}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>

          {/* Add-ons Grid */}
          <div className='mb-20'>
            <h2 className='text-3xl font-black mb-10 text-center text-gradient'>
              {content.addOnsTitle}
            </h2>
            <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              {addOns.map((add, i) => (
                <GlassCard
                  key={i}
                  className='p-5 flex flex-col items-center text-center'
                >
                  <h3 className='font-bold mb-1'>{add.name}</h3>
                  <span className='text-royal-purple-600 font-black mb-4'>
                    {add.price}
                  </span>
                  <button
                    onClick={() => openWhatsApp(add.name)}
                    className='w-full py-2 text-xs font-black glass-button uppercase tracking-widest'
                  >
                    {content.addToPkg}
                  </button>
                </GlassCard>
              ))}
            </div>
          </div>

          {/* Benefits Footer */}
          <div className='grid md:grid-cols-4 gap-6'>
            {content.benefits.map((b, i) => (
              <GlassCard key={i} className='p-6 text-center' lang={lang}>
                <div className='inline-flex p-3 premium-gradient rounded-xl mb-4 shadow-lg'>
                  {b.icon}
                </div>
                <h3 className='font-black mb-1'>{b.title}</h3>
                <p className='text-xs text-gray-500 leading-relaxed'>
                  {b.description}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagesPage;
